import React, { useState } from 'react';
import AppLayout from 'layouts/AppLayout';
import LeftPanel from './components/LeftPanel';
import CentrePanel from './components/CentrePanel';
import RightPanel from './components/RightPanel';
import AnnouncementView from './components/AnnouncementView';
import NewMessageModal from './components/NewMessageModal';
import DocumentSharingModal from './components/DocumentSharingModal';
import { THREADS } from './components/communicationsData';
import Icon from 'components/AppIcon';

export default function CommunicationsHub() {
  const [activeThread, setActiveThread] = useState(THREADS?.[0]);
  const [activeAnnouncement, setActiveAnnouncement] = useState(null);
  const [activeDealRoom, setActiveDealRoom] = useState(null);
  const [centreMode, setCentreMode] = useState('thread'); // 'thread' | 'announcement' | 'dealroom'
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [sendToast, setSendToast] = useState(false);

  const handleSelectThread = (thread) => {
    setActiveThread(thread);
    setCentreMode('thread');
    setActiveAnnouncement(null);
    setActiveDealRoom(null);
  };

  const handleSelectAnnouncement = (ann) => {
    setActiveAnnouncement(ann);
    setCentreMode('announcement');
    setActiveThread(null);
    setActiveDealRoom(null);
  };

  const handleSelectDealRoom = (room) => {
    // Find matching thread for deal room
    const matchingThread = THREADS?.find(t => t?.dealLinked === room?.name);
    if (matchingThread) {
      setActiveThread(matchingThread);
      setCentreMode('thread');
    } else {
      setActiveDealRoom(room);
      setCentreMode('dealroom');
    }
    setActiveAnnouncement(null);
  };

  const handleSendMessage = ({ to, subject, linkedDeal, body }) => {
    setShowCompose(false);
    setSendToast(true);
    setTimeout(() => setSendToast(false), 3000);
  };

  const handleAttachDoc = (docs) => {
    // Docs attached — in real app would add to composer
  };

  return (
    <AppLayout>
      <div className="flex h-screen overflow-hidden bg-background relative">
        {/* Toast */}
        {sendToast && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-medium shadow-lg">
            <Icon name="CheckCircle" size={16} />
            Message sent
          </div>
        )}

        {/* Left Panel */}
        <div className="flex-shrink-0 overflow-hidden" style={{ width: 260 }}>
          <LeftPanel
            activeThreadId={activeThread?.id}
            activeAnnId={activeAnnouncement?.id}
            activeDealRoomId={activeDealRoom?.id}
            onSelectThread={handleSelectThread}
            onSelectAnnouncement={handleSelectAnnouncement}
            onSelectDealRoom={handleSelectDealRoom}
            onCompose={() => setShowCompose(true)}
          />
        </div>

        {/* Centre Panel */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {centreMode === 'thread' && (
            <CentrePanel
              thread={activeThread}
              onToggleRightPanel={() => setRightPanelOpen(o => !o)}
              rightPanelOpen={rightPanelOpen}
              onAttach={() => setShowDocModal(true)}
            />
          )}
          {centreMode === 'announcement' && (
            <AnnouncementView announcement={activeAnnouncement} />
          )}
          {centreMode === 'dealroom' && (
            <div className="flex-1 flex items-center justify-center bg-slate-50">
              <div className="text-center">
                <div className="w-14 h-14 rounded-2xl bg-[#3b4c4c]/8 flex items-center justify-center mx-auto mb-3">
                  <Icon name="Hash" size={28} className="text-[#3b4c4c]/40" />
                </div>
                <h3 className="text-base font-semibold text-slate-700 mb-1">{activeDealRoom?.name} Deal Room</h3>
                <p className="text-sm text-slate-400">{activeDealRoom?.participants} participants · {activeDealRoom?.stage}</p>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel */}
        {rightPanelOpen && centreMode === 'thread' && (
          <div className="flex-shrink-0 overflow-hidden" style={{ width: 300 }}>
            <RightPanel
              thread={activeThread}
              onClose={() => setRightPanelOpen(false)}
            />
          </div>
        )}
      </div>

      {/* Modals */}
      {showCompose && (
        <NewMessageModal
          onClose={() => setShowCompose(false)}
          onSend={handleSendMessage}
        />
      )}
      {showDocModal && (
        <DocumentSharingModal
          onClose={() => setShowDocModal(false)}
          onAttach={handleAttachDoc}
        />
      )}
    </AppLayout>
  );
}
